// Leitura do catálogo servido pelo backend (GET /disparos/catalogo/*).
//
// Substitui os catálogos estáticos que o protótipo mantinha no front
// (canal-provedor, filtro-cronjob, variaveis-mensagem): o que pode ser
// filtrado, escrito num template ou usado como contato agora é allowlist do
// backend, não lista nossa. Aqui só há leitura — nada é inventado.

import {
  CanalComunicacao,
  CatalogoDisparo,
  ContextoDisparo,
  DestinatarioDisparo,
  EntidadeCatalogo,
  EventoCatalogo,
  FiltroDisparo,
  FormatoVariavel,
  FrequenciaCatalogo,
  FrequenciaDisparo,
  ModoTemplate,
  OperadorFiltro,
  PropriedadeCatalogo,
  ProvedorComunicacao,
  RelacaoCatalogo,
  TipoPropriedade,
  rotuloOperador,
  tipoContatoDoCanal,
} from "@/models/consignado/disparo.model";
import { descreverDataRelativa, ehDataRelativa } from "./data-relativa";
import { CobrancaCltEnum, CobrancaCltEnumPt } from "@/models/consignado/proposal-billings.model";
import { proposalConsignadoStatusLabels } from "@/utils/consignado/getProposalStatusLabel";
import { AUCTION_PROPOSAL_STATUS_LABELS } from "@/utils/auctionProposalStatus";

export const entidadeDoContexto = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): EntidadeCatalogo | undefined => catalogo?.entidades.find((e) => e.id === contexto);

export const propriedadesDoContexto = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): PropriedadeCatalogo[] => entidadeDoContexto(catalogo, contexto)?.propriedades ?? [];

export const propriedade = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
  id: string,
): PropriedadeCatalogo | undefined =>
  propriedadesDoContexto(catalogo, contexto).find((p) => p.id === id);

/** Só as que têm operador declarado — hoje status e as datas. */
export const propriedadesFiltraveis = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): PropriedadeCatalogo[] =>
  propriedadesDoContexto(catalogo, contexto).filter((p) => p.disponivelFiltro);

// ---------------------------------------------------------------------------
// Rótulo dos valores de status
//
// O catálogo publica o código do status (`PENDING_CONFIRMATION`) e é ele que
// vai no filtro. Na tela, quem cadastra lê o mesmo nome que vê na tela de
// propostas, de leilão ou de cobrança — os mapas são os dessas telas, não
// um terceiro. Código sem rótulo em nenhum deles aparece cru, como antes.
// ---------------------------------------------------------------------------

const capitalizar = (texto: string) => texto.charAt(0) + texto.slice(1).toLowerCase();

const ROTULO_STATUS_COBRANCA: Record<string, string> = {
  [CobrancaCltEnum.PENDING]: capitalizar(CobrancaCltEnumPt.PENDENTE),
  [CobrancaCltEnum.SUCCESS]: capitalizar(CobrancaCltEnumPt.SUCESSO),
  [CobrancaCltEnum.DIVERGENT]: capitalizar(CobrancaCltEnumPt.DIVERGENTE),
  [CobrancaCltEnum.SETTLED]: capitalizar(CobrancaCltEnumPt.LIQUIDADO),
  [CobrancaCltEnum.CANCELED]: capitalizar(CobrancaCltEnumPt.CANCELADO),
};

const ROTULOS_DE_VALOR: Record<ContextoDisparo, Record<string, Record<string, string>>> = {
  [ContextoDisparo.PROPOSTA]: { status: proposalConsignadoStatusLabels },
  [ContextoDisparo.PROPOSTA_LEILAO]: { status: AUCTION_PROPOSAL_STATUS_LABELS },
  [ContextoDisparo.COBRANCA]: {
    status: ROTULO_STATUS_COBRANCA,
    statusEscrituracao: ROTULO_STATUS_COBRANCA,
    statusRepasse: ROTULO_STATUS_COBRANCA,
    statusCompetencia: ROTULO_STATUS_COBRANCA,
  },
};

const ROTULO_SIM_NAO: Record<string, string> = { true: "Sim", false: "Não" };

const ROTULOS_POR_PROPRIEDADE: Record<string, Record<string, string>> = {
  "tomador.temTelefoneInformado": ROTULO_SIM_NAO,
};

/** `PENDING_CONFIRMATION` → "Aguardando Finalizar Digitação" (em Proposta). */
export const rotuloDoValor = (
  contexto: ContextoDisparo,
  propriedadeId: string,
  valor: string,
): string =>
  ROTULOS_DE_VALOR[contexto]?.[propriedadeId]?.[valor] ??
  ROTULOS_POR_PROPRIEDADE[propriedadeId]?.[valor] ??
  valor;

/** Operador e valores como a pílula do filtro mostra: "Entre 20 – 100", "No período 7 dias atrás – agora". */
export const detalheDaCondicao = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
  filtro: Pick<FiltroDisparo, "propriedade" | "operador" | "valores">,
): string => {
  const tipo = propriedade(catalogo, contexto, filtro.propriedade)?.tipo;
  const valores = filtro.valores
    .filter((v) => v.trim() !== "")
    .map((v) => (ehDataRelativa(v) ? descreverDataRelativa(v) : rotuloDoValor(contexto, filtro.propriedade, v)))
    .join(filtro.operador === OperadorFiltro.ENTRE ? " – " : ", ");
  return valores ? `${rotuloOperador(filtro.operador, tipo)} ${valores}` : rotuloOperador(filtro.operador, tipo);
};

/**
 * Colunas da prévia da busca. O código curto da proposta entra no lugar do id
 * quando o contexto o tem: é por ele que o tomador e o atendimento acham o
 * registro. Do próprio contexto primeiro (`codigoCurto`), senão o de uma
 * relação (`proposta.codigoCurto`, `cobranca.codigoCurto`).
 */
export const colunasDaPrevia = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): string[] => {
  const props = propriedadesDoContexto(catalogo, contexto);
  const codigos = props.filter((p) => /codigoCurto$/i.test(p.id));
  const codigo = codigos.find((p) => !p.id.includes(".")) ?? codigos[0];
  return [codigo?.id ?? "id", "status", "criadoEm"].filter((id) => props.some((p) => p.id === id));
};

export const propriedadesDeTemplate = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): PropriedadeCatalogo[] =>
  propriedadesDoContexto(catalogo, contexto).filter((p) => p.disponivelTemplate);

// ---------------------------------------------------------------------------
// Grupos por entidade
//
// As propriedades vêm achatadas (`tomador.nome`, `status`). O prefixo antes
// do ponto é a relação declarada pelo backend em `relacoes`; propriedade sem
// ponto é da entidade raiz do contexto. É o que permite escolher "de qual
// entidade" vem a variável, sem inventar entidade nenhuma.
// ---------------------------------------------------------------------------

export const GRUPO_RAIZ = "__raiz__";

export const grupoDaPropriedade = (id: string): string => {
  const ponto = id.indexOf(".");
  return ponto === -1 ? GRUPO_RAIZ : id.slice(0, ponto);
};

/**
 * Rótulos das relações. O nome de negócio vem do catálogo, em
 * `relacoes[].descricao`; este mapa é só a reserva para catálogo que não o
 * traga, com os mesmos textos que o backend publica. Relação sem nenhum dos
 * dois cai no próprio id, capitalizado, em vez de sumir — era assim que
 * "Solicitacao" e "Competencias" apareciam cruas na barra lateral.
 */
const ROTULO_GRUPO: Record<string, string> = {
  tomador: "Tomador",
  empregador: "Empregador",
  financeiro: "Informação financeira",
  banco: "Banco da conta",
  seguro: "Informação de seguro (apólice)",
  seguradora: "Seguradora",
  solicitacao: "Solicitação do leilão (histórico)",
  competencias: "Competências (parcelas)",
  "empregador.contatos": "Contatos do RH",
};

export const rotuloDoGrupo = (
  grupo: string,
  rotuloRaiz: string,
  relacoes?: RelacaoCatalogo[],
): string => {
  if (grupo === GRUPO_RAIZ) return rotuloRaiz;
  const doCatalogo = relacoes?.find((r) => r.id === grupo)?.descricao;
  if (doCatalogo) return doCatalogo;
  if (ROTULO_GRUPO[grupo]) return ROTULO_GRUPO[grupo];
  return grupo
    .split(".")
    .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
    .join(" · ");
};

export interface GrupoEntidade {
  id: string;
  rotulo: string;
  /** Quantas propriedades do grupo estão na lista recebida. */
  quantidade: number;
  /** Relação que o backend declara mas não deixa usar — vem desabilitada. */
  selecionavel: boolean;
  motivo?: string;
}

/**
 * Entidades de onde as propriedades podem vir, na ordem: raiz primeiro,
 * relações depois.
 *
 * Relação declarada com `selecionavel: false` (Contatos do RH, Competências)
 * fica de fora. Ela já apareceu como pill bloqueada, com o motivo no tooltip,
 * mas quem monta a mensagem não tem o que fazer com ela — o plano de teste
 * pede que não apareça.
 */
export const gruposDoContexto = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
  propriedades: PropriedadeCatalogo[],
): GrupoEntidade[] => {
  const entidade = entidadeDoContexto(catalogo, contexto);
  if (!entidade) return [];

  const contar = (id: string) =>
    propriedades.filter((p) => grupoDaPropriedade(p.id) === id).length;

  const raiz: GrupoEntidade = {
    id: GRUPO_RAIZ,
    rotulo: entidade.descricao,
    quantidade: contar(GRUPO_RAIZ),
    selecionavel: true,
  };

  const relacoes = entidade.relacoes
    .filter((r) => r.selecionavel)
    .map((r) => ({
      id: r.id,
      rotulo: rotuloDoGrupo(r.id, entidade.descricao, entidade.relacoes),
      quantidade: contar(r.id),
      selecionavel: r.selecionavel,
      motivo: r.motivo,
    }));

  // Grupo sem propriedade não tem o que oferecer — fica de fora.
  return [raiz, ...relacoes].filter((g) => g.quantidade > 0);
};

/**
 * Propriedades que servem de origem de contato: o backend exige que sejam
 * do destinatário do disparo e do tipo que o canal usa (EMAIL ou TELEFONE).
 */
export const propriedadesDeContato = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
  destinatario: DestinatarioDisparo,
  canal: CanalComunicacao,
): PropriedadeCatalogo[] => {
  const tipo = tipoContatoDoCanal(canal);
  return propriedadesDoContexto(catalogo, contexto).filter(
    (p) => p.contato === destinatario && p.tipo === tipo,
  );
};

/**
 * Contato de quem recebe. A tela não escolhe mais: o backend decide pelo
 * destinatário e pelo canal (resolverOrigemContato, no consignado-trabalhador)
 * e ignora o que vier. A regra é repetida aqui só para mostrar qual será e para
 * mandar o mesmo valor ao backend que ainda exige o campo.
 */
export const contatoAutomatico = (
  destinatario: DestinatarioDisparo,
  canal: CanalComunicacao,
): string => {
  const email = canal === CanalComunicacao.EMAIL;
  if (destinatario === DestinatarioDisparo.TOMADOR) return email ? "tomador.email" : "tomador.telefone";
  return email ? "empregador.email" : "rh.telefone";
};

const ROTULO_CONTATO_AUTOMATICO: Record<string, string> = {
  "tomador.email": "e-mail do tomador",
  "tomador.telefone": "telefone do tomador",
  "empregador.email": "e-mail cadastral do empregador",
  "rh.telefone": "telefone do contato do RH",
};

export const rotuloContatoAutomatico = (
  destinatario: DestinatarioDisparo,
  canal: CanalComunicacao,
): string => {
  const id = contatoAutomatico(destinatario, canal);
  return ROTULO_CONTATO_AUTOMATICO[id] ?? id;
};

/** O contato automático, se o contexto o publica para o destinatário e o canal. */
export const contatoAutomaticoDoContexto = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
  destinatario: DestinatarioDisparo,
  canal: CanalComunicacao,
): PropriedadeCatalogo | undefined => {
  const id = contatoAutomatico(destinatario, canal);
  return propriedadesDeContato(catalogo, contexto, destinatario, canal).find((p) => p.id === id);
};

/** Eventos do catálogo vivo que aceitam este contexto. */
export const eventosDoContexto = (
  catalogo: CatalogoDisparo | undefined,
  contexto: ContextoDisparo,
): EventoCatalogo[] => (catalogo?.eventos ?? []).filter((e) => e.contextos.includes(contexto));

export const evento = (
  catalogo: CatalogoDisparo | undefined,
  id?: string,
): EventoCatalogo | undefined => (id ? catalogo?.eventos.find((e) => e.id === id) : undefined);

/** Canais que algum provedor cadastrado atende. */
export const canaisDisponiveis = (catalogo: CatalogoDisparo | undefined): CanalComunicacao[] => {
  const canais = new Set<CanalComunicacao>();
  (catalogo?.canais ?? []).forEach((p) =>
    p.capacidades.forEach((c) => canais.add(c.canal)),
  );
  return [...canais];
};

/** Provedores que atendem o canal (WhatsApp tem dois; e-mail e SMS, um cada). */
export const provedoresDoCanal = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
): ProvedorComunicacao[] =>
  (catalogo?.canais ?? [])
    .filter((p) => p.capacidades.some((c) => c.canal === canal))
    .map((p) => p.provedor);

const capacidade = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
  provedor: ProvedorComunicacao,
) =>
  (catalogo?.canais ?? [])
    .find((p) => p.provedor === provedor)
    ?.capacidades.find((c) => c.canal === canal);

/** Modos de template que a combinação canal × provedor suporta. */
export const modosDoProvedor = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
  provedor: ProvedorComunicacao,
): ModoTemplate[] => capacidade(catalogo, canal, provedor)?.modos ?? [];

/** Remetentes aceitos — Meta tem quatro (esteira, cobrança…); o resto só PADRAO. */
export const remetentesDoProvedor = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
  provedor: ProvedorComunicacao,
): string[] => capacidade(catalogo, canal, provedor)?.remetentes ?? [];

/** Nome do número por trás de cada código legado de remetente do WhatsApp. */
const ROTULO_REMETENTE_LEGADO: Record<string, string> = {
  ESTEIRA: "Esteira - Análise Banco / Correção de Dados / Formalização",
  COBRANCA: "Esteira - Cobrança",
  PROSPECCAO_LEILAO: "Leilão - Fruta mais baixa",
  AUTENTICACAO: "Esteira - Autenticação",
};

/**
 * Casa o remetente salvo com a opção que o catálogo publica hoje.
 *
 * Cadastros antigos gravaram o código (`ESTEIRA`); o catálogo agora devolve
 * "Esteira - Cobrança (+55 11 95502-5480)", para quem cadastra ver de qual
 * número a mensagem sai. Sem esta tradução o select abre vazio num disparo que
 * é válido — e o backend aceita as duas formas, então nada acusaria o problema.
 */
export const remetenteDoCatalogo = (
  salvo: string,
  remetentes: string[],
): string | undefined => {
  if (!salvo) return undefined;
  if (remetentes.includes(salvo)) return salvo;
  const rotulo = ROTULO_REMETENTE_LEGADO[salvo];
  if (!rotulo) return undefined;
  return remetentes.find((r) => r === rotulo || r.startsWith(`${rotulo} (`));
};

/**
 * Teto de caracteres do corpo próprio nessa combinação canal × provedor.
 * `undefined` = sem limite publicado.
 */
export const limiteCaracteresDoProvedor = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
  provedor: ProvedorComunicacao,
): number | undefined => capacidade(catalogo, canal, provedor)?.limiteCaracteres;

/** Campos que a referência externa desse provedor recebe (identificador, idioma, produtoId…). */
export const camposExternosDoProvedor = (
  catalogo: CatalogoDisparo | undefined,
  canal: CanalComunicacao,
  provedor: ProvedorComunicacao,
): string[] => capacidade(catalogo, canal, provedor)?.camposExternos ?? [];

/** Formatos compatíveis com o tipo da propriedade. */
export const formatosDoTipo = (
  catalogo: CatalogoDisparo | undefined,
  tipo: TipoPropriedade,
): FormatoVariavel[] =>
  (catalogo?.formatacoes ?? []).filter((f) => f.tipos.includes(tipo)).map((f) => f.id);

export const exemploDoFormato = (
  catalogo: CatalogoDisparo | undefined,
  formato: FormatoVariavel,
): string | undefined => catalogo?.formatacoes.find((f) => f.id === formato)?.exemplo;

export const frequenciaSpec = (
  catalogo: CatalogoDisparo | undefined,
  frequencia: FrequenciaDisparo | null,
): FrequenciaCatalogo | undefined =>
  frequencia ? catalogo?.agendamentos.frequencias.find((f) => f.id === frequencia) : undefined;

/** Campos que a frequência exige — o backend recusa qualquer outro. */
export const camposDaFrequencia = (
  catalogo: CatalogoDisparo | undefined,
  frequencia: FrequenciaDisparo | null,
): string[] => frequenciaSpec(catalogo, frequencia)?.campos ?? [];

/**
 * Formato com que a variável nasce ao ser inserida pela barra lateral.
 *
 * O catálogo lista os formatos permitidos de cada campo com Texto sempre em
 * primeiro. Pegar o primeiro fazia valor em reais sair como "1234.5", data como
 * o valor cru do banco e o CPF inteiro, sem máscara. O padrão sai do tipo do
 * campo; nos numéricos, do nome, porque o tipo não separa dinheiro de
 * quantidade. O formato continua trocável na lista "De onde vem cada variável"
 * — é só a máscara aplicada no envio, o valor é o mesmo.
 *
 * Se o padrão não estiver entre os formatos que o catálogo permite para o
 * campo, cai no primeiro da lista.
 */
export const formatoPadrao = (p: PropriedadeCatalogo): FormatoVariavel => {
  const nome = p.id.split(".").pop() ?? p.id;
  const sugerido = (() => {
    switch (p.tipo) {
      case TipoPropriedade.NUMERO:
        if (/taxa|cet/i.test(nome)) return FormatoVariavel.DECIMAL_BR;
        if (/valor|iof|margem|^parcela$/i.test(nome)) return FormatoVariavel.MOEDA_BRL;
        return FormatoVariavel.NUMERO;
      case TipoPropriedade.DATA:
        return /competencia/i.test(nome) ? FormatoVariavel.COMPETENCIA : FormatoVariavel.DATA_BR;
      case TipoPropriedade.CPF:
        return FormatoVariavel.CPF_MASCARADO;
      case TipoPropriedade.CNPJ:
        return FormatoVariavel.CNPJ;
      default:
        return FormatoVariavel.TEXTO;
    }
  })();
  return p.formatos.includes(sugerido) ? sugerido : p.formatos[0] ?? FormatoVariavel.TEXTO;
};
